import { Dispatch, FC, SetStateAction } from 'react';
import { IBatch, IBinLocation, IGoodsReceiptHeader, IGoodsReceiptRow, IIDeliveryDao$IDeliveryDocument, IIItemDao$ICompanion$IStockTransferRequest, IIProductionDao$ISaveTimeWorkedFlushingResult, IItem, IKardexSettings, IOrderHeader, IOrderLine, IPicklistCandidateGroup, IPickListHeader, IPickListRow, IPickListsShippingContainer, IProductionWorkTime, ISerialNumber, IWarehouse, IWorkOrder, IWorkOrderStep } from 'interfaces';
import { IRouteDefinition } from 'statics/route_data';
import { GridAutoFixer } from 'components/datasheetUtils/fixers';
import { MaybePromise } from 'component_utils/utils';
import { Column } from 'components/BeTable';
import { ActionBarcodeGeneratorDefinition } from 'routes/setup/ActionBarcodeGenerator';
import { ScannerDropdownItem } from 'components/Scanner';
import { ResolvedStockLine } from 'components/StateMachine/states/groups/itemIdentification';

export interface SpecializationPoints {
  identifier: string;
  routesCustomizer?: (routes: IRouteDefinition[]) => IRouteDefinition[];

  customSettingsConfigurator?: FC<{
    state: any;
    setState: React.Dispatch<React.SetStateAction<any>>
  }>

  dataGridAutofixers?: () => GridAutoFixer[],
  
  kardexSettings?: FC<{
    kardexSettings: IKardexSettings;
    setKardexSettings: React.Dispatch<React.SetStateAction<IKardexSettings>>;
  }>;

  customActionBarcodes?: {
    definitions?: ActionBarcodeGeneratorDefinition[];
    onScan?: (s: string) => Promise<any>;
  }

  augmentations?: {
    deviceSettings: {
      userPrinters: FC<{ renderPrinterField: any }>;
      userFields: FC<{ renderPrinterField: any }>;
    },
    identification?: {
      itemSelected?: (args: { item: IItem }) => MaybePromise<any>;
      batchSelected?: (args: { item: IItem, batch: IBatch }) => MaybePromise<any>;
      lotSelected?: (args: { item: IItem, lot: string }) => MaybePromise<any>;
      serialSelected?: (args: { item: IItem, serial: ISerialNumber }) => MaybePromise<any>;
    }
    objects?: {
      item?: {
        mainTab?: FC<{ item: IItem }>;
        actionsTab?: FC<{ item: IItem }>;
      };
      binLocation?: {
        mainTab?: FC<{ binLocation: IBinLocation }>;
        actionsTab?: FC<{ binLocation: IBinLocation }>;
      };
      batch?: {
        mainTab?: FC<{ batch: IBatch }>;
        actionsTab?: FC<{ batch: IBatch }>;
      };
      serial?: {
        mainTab?: FC<{ serial: ISerialNumber }>;
        actionsTab?: FC<{ serial: ISerialNumber }>;
      };
      warehouse?: {
        mainTab?: FC<{ warehouse: IWarehouse }>;
      };
    },
    warehouse?: {
      stockTransfer?: {
        stockTransferValidation?: (args: { appName: string, verified: IIItemDao$ICompanion$IStockTransferRequest[] }) => MaybePromise<any>
      },
    },
    inbound?: {
      goodsReceipt?: {
        orderSelectionOnScan?: (args: { input: string, orders: IGoodsReceiptHeader[] }) => MaybePromise<string>
        processingExtraButtons?: (args: { order: IGoodsReceiptHeader, lines: IGoodsReceiptRow[] }) => ScannerDropdownItem[],
      }
    },
    production?: {
      timeBooking?: {
        onWorkOrderCodeChange?: (newValue: string, foundWorkOrder: IWorkOrder, setState: Dispatch<SetStateAction<IProductionWorkTime>>, setSteps: Dispatch<SetStateAction<IWorkOrderStep[]>>) => MaybePromise<boolean>;
        extraColumns?: Column<any, any, any>[],
        extraActionButtons?: FC<{ reload: any; isWorkVerification: boolean; }>;
        customFields?: FC<{ state: IProductionWorkTime, setState: Dispatch<SetStateAction<IProductionWorkTime>> }>
        afterFlush?: (results: IIProductionDao$ISaveTimeWorkedFlushingResult[]) => any;
      },
      materialConfirmation?: {
        stepSelectionExtraButtons?: (args: { order: IWorkOrder, steps: IWorkOrderStep[], licensePlate: string }) => ScannerDropdownItem[],
      }
    },
    outbound?: {
      picklistGenerator?: {
        table?: {
          groupColumn?: {
            ExtensionComponent?: FC<{ 
              candidateGroup: IPicklistCandidateGroup;
              reloadCandidates: any;
            }>
          },
          extraColumns?: Column<any, any, any>[],
        }
      },
      pickAndCollect?: {
        onStartCollectingOrders(toPickState: { orders: IOrderHeader[]; pickAllFromLocation: IBinLocation; }): unknown;
        onOrderSelected?: (order: IOrderHeader) => MaybePromise<void>,
        extraColumns?: Column<any, any, any>[],
        
        processing?: {
          extraActions: (args: { orders: IOrderHeader[], lines: IOrderLine[], locations: IBinLocation[] }) => ScannerDropdownItem[],
          filterTransfer: (args: { lines: ResolvedStockLine[] }) => MaybePromise<any>
        }
      },
      picking?: {
        afterItemPicked?: (
          picklistCode: string, 
          location: IBinLocation,
          item: IItem, 
          batch: IBatch, 
          serial: ISerialNumber,
          scannedMnfSerials: string[],
          quantity: number
        ) => any;
      },
      packing?: {
        containerCustomFooter?: FC<{
          header: IPickListHeader;
          container: IPickListsShippingContainer;
          setContainer: (s: IPickListsShippingContainer) => any;
          lines: IPickListRow[];
        }>,
        onContainerAdded?: (args: {
          header: IPickListHeader;
          lines: IPickListRow[];
          container: IPickListsShippingContainer;
          setContainer: (s: IPickListsShippingContainer) => any;
        }) => MaybePromise<any>
      },
      delivery?: {
        beforeDelivery?: (args: {
          lines: IPickListRow[];
          shippingContainers: IPickListsShippingContainer[];
          userdata: any;
        }) => MaybePromise<any>;
        afterDelivery?: (args: {
          lines: IPickListRow[];
          shippingContainers: IPickListsShippingContainer[];
          userdata: any;
          deliveryCode: string;
        }) => MaybePromise<any>;
      },
      deliveryDispatchSorter?: {
        CardCustomFields?: FC<{
          delivery: IIDeliveryDao$IDeliveryDocument 
        }>
        customFilter?: {
          Component: FC<{}>
          applyFilter: (record: IIDeliveryDao$IDeliveryDocument, filter: any) => boolean
        }
        onPrint?: (args: { printData: any, group: string, deliveries: IIDeliveryDao$IDeliveryDocument[] }) => MaybePromise<any>
      },
      packingSlip?: {
        extraColumns?: Column<any, any, any>[],
      },
    }
  }
}

export const CostumerIdMappings: { [k: string]: () => Promise<any> } = {
  lankhorst_taselaar: () => import(/* webpackChunkName: "specialization/lankhorst_taselaar/index" */ 'specialization/lankhorst_taselaar'),
  HCM: () => import(/* webpackChunkName: "specialization/hcm/index" */ 'specialization/hcm'),
  ILS: () => import(/* webpackChunkName: "specialization/ils/index" */ 'specialization/ils'),
};
